import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import caseMembersService from '../../services/caseMembersService';
import AddMemberModal from './AddMemberModal';
import { usePlan } from '../../context/PlanContext';
import UpgradePrompt from '../shared/UpgradePrompt';

/**
 * CaseMembers Component
 * Display and manage team members on a case
 * 
 * Props:
 *   - caseId: Case UUID
 *   - currentUserId: Current user's ID
 *   - isLead: Whether current user is Lead (can manage members)
 */
const CaseMembers = ({ caseId, currentUserId, isLead }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  const isEnglish = currentLanguage === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const { canUseFeature } = usePlan();

  const hasTeamFeature = canUseFeature('can_add_team_members');

  useEffect(() => {
    if (caseId) {
      fetchMembers();
    }
  }, [caseId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await caseMembersService.getCaseMembers(caseId);
      setMembers(data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error(t('Could not load team members', 'Kunne ikke laste teammedlemmer'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (userId, role) => {
    try {
      await caseMembersService.addMember(caseId, userId, role);
      toast.success(t('Team member added', 'Teammedlem lagt til'));
      fetchMembers();
      setShowAddModal(false);
    } catch (error: any) {
      console.error('Failed to add member:', error);
      const message = error.response?.data?.detail || t('Could not add member', 'Kunne ikke legge til medlem');
      
      if (error.response?.status === 403) {
        toast.error(`${message} - ${t('Click here to upgrade', 'Klikk her for å oppgradere')}`, {
          duration: 5000
        });
      } else {
        toast.error(message);
      }
    }
  };

  const handleRemoveMember = async (userId: string, userName: string) => {
    if (!window.confirm(t(`Are you sure you want to remove ${userName} from the case?`, `Er du sikker på at du vil fjerne ${userName} fra saken?`))) {
      return;
    }

    try {
      await caseMembersService.removeMember(caseId, userId);
      toast.success(t('Team member removed', 'Teammedlem fjernet'));
      fetchMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || t('Could not remove member', 'Kunne ikke fjerne medlem'));
    }
  };

  const handleChangeRole = async (userId, currentRole, userName) => {
    const roles = ['ASSISTANT', 'PARALEGAL', 'EXPERT', 'EXTERNAL'];
    const newRole = window.prompt(
      t(
        `Change role for ${userName}\n\nAvailable roles:\n${roles.join(', ')}\n\nCurrent: ${currentRole}`,
        `Endre rolle for ${userName}\n\nTilgjengelige roller:\n${roles.join(', ')}\n\nNåværende: ${currentRole}`
      ),
      currentRole
    );

    if (!newRole || newRole === currentRole) return;

    if (!roles.includes(newRole.toUpperCase())) {
      toast.error(t('Invalid role', 'Ugyldig rolle'));
      return;
    }

    try {
      await caseMembersService.updateMemberRole(caseId, userId, newRole.toUpperCase());
      toast.success(t('Role updated', 'Rolle oppdatert'));
      fetchMembers();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || t('Could not update role', 'Kunne ikke oppdatere rolle'));
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      LEAD: 'bg-gray-100 text-indigo-800',
      ASSISTANT: 'bg-blue-100 text-blue-800',
      PARALEGAL: 'bg-green-100 text-green-800',
      EXPERT: 'bg-purple-100 text-purple-800',
      EXTERNAL: 'bg-yellow-100 text-yellow-800',
      CLIENT: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      LEAD: t('Lead', 'Hovedansvarlig'),
      ASSISTANT: t('Associate', 'Medarbeider'),
      PARALEGAL: 'Paralegal',
      EXPERT: t('Expert', 'Ekspert'),
      EXTERNAL: t('External', 'Ekstern'),
      CLIENT: t('Client', 'Klient')
    };
    return labels[role] || role;
  };

  if (!hasTeamFeature && members.length === 0) {
    return (
      <div className="mt-6">
        <UpgradePrompt feature="team_members" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('Team Members', 'Teammedlemmer')} ({members.length})
        </h3>
        
        {isLead ? (
          hasTeamFeature ? (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              + {t('Add Member', 'Legg til medlem')}
            </button>
          ) : (
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-1">{t('Team Collaboration requires Team plan', 'Team Collaboration krever Team-plan')}</p>
              <button
                onClick={() => window.location.href = '/settings/billing'}
                className="px-3 py-1.5 bg-gray-100 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-200 transition-colors"
              >
                {t('Upgrade to Team', 'Oppgrader til Team')}
              </button>
            </div>
          )
        ) : (
          <p className="text-xs text-gray-500">{t('Only lead can add members', 'Kun hovedansvarlig kan legge til medlemmer')}</p>
        )}
      </div>

      {/* Members List */}
      {members.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          {t('No team members yet', 'Ingen teammedlemmer ennå')}
        </p>
      ) : (
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {/* Avatar - with profile picture support */}
              <div className="flex items-center space-x-3">
                {member.profile_picture_url || member.avatar_url ? (
                  <img
                    src={member.profile_picture_url || member.avatar_url}
                    alt={member.user_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {member.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}

                {/* Details */}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {member.user_name || 'Unknown'}
                    {member.user_id === currentUserId && (
                      <span className="ml-2 text-xs text-gray-500">({t('you', 'deg')})</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">{member.user_email}</p>
                </div>

                {/* Role Badge */}
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(member.role)}`}>
                  {getRoleLabel(member.role)}
                </span>
              </div>

              {/* Actions */}
              {isLead && member.role !== 'LEAD' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleChangeRole(member.user_id, member.role, member.user_name)}
                    className="text-sm text-gray-600 hover:text-indigo-600 transition-colors"
                    title={t('Change role', 'Endre rolle')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                    className="text-sm text-gray-600 hover:text-red-600 transition-colors"
                    title={t('Remove member', 'Fjern medlem')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          caseId={caseId}
          existingMembers={members}
          onAdd={handleAddMember}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

export default CaseMembers;
